import { useEffect, useState, memo } from "react";
import FormErrors from "components/formParts/formErrors";
import { useTranslation } from "react-i18next";
import { decodeFirebaseError } from "utils/firebaseHelpers";
import FileInput from "components/inputs/fileInput";
import Button from "components/inputs/button";
import useLayout from "hooks/useLayout";
import useAuth from "hooks/useAuth";
import Form from "components/forms/form"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg";
import ProcessingButton from "components/inputs/processingButton";
import { logger } from "utils/secureLogger";
import Section from "components/layout/section";
import Cropper, { Area } from 'react-easy-crop'
import { getCroppedImg } from "@/utils/cropImage";
import { CirclePicker } from 'react-color';
import { AccountPublicWrite } from "@/types";

const AccountAvatarForm = () => {

    const { t } = useTranslation()

    const [file, setFile] = useState<File | null>(null)
    const [croppedFile, setCroppedFile] = useState<File | null>(null)
    const [avatarColor, setAvatarColor] = useState<string>("#3B82F6")

    // Cropper
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Auth hooks.
    const { updateAccountAvatar, isUpdatingAccountAvatar, accountPublicData } = useAuth()

    // Layout hooks.
    const { addToast } = useLayout()

    // Update the form fields with the user doc data.
    useEffect(() => {
        if (accountPublicData) {
            setAvatarColor(accountPublicData.other?.avatarColor || "#3B82F6")
        }
    }, [accountPublicData])

    // Handle form submission.
    const handleSubmit = async (e: React.FormEvent) => {
        // Prevent the form from refreshing the page.
        e.preventDefault()
        setErrors([])
        
        try {
            await updateAccountAvatar({
                file: croppedFile,
                other: {
                    avatarColor: avatarColor,
                }
            } as AccountPublicWrite);
            addToast({ text: t('toastAvatarUpdated'), colorScheme: 'green' })
            // Reset file inputs after successful upload
            setFile(null)
            setCroppedFile(null)
        } catch (error: any) {
            logger.error('Account avatar form error', error, 'AccountAvatar')
            const errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastAvatarError'), colorScheme: 'red' })
        }
    }

    const onCropComplete = async (croppedArea: Area, croppedAreaPixels: Area) => {
        if (!file) return
        try {
            const croppedUrl = file ? URL.createObjectURL(file) : null
            if (!croppedUrl) {
                throw new Error("No image file selected")
            }
            const croppedBlob = await getCroppedImg(croppedUrl, croppedAreaPixels)
            const croppedFile = new File([croppedBlob], file.name, { type: file.type })
            setCroppedFile(croppedFile)
        } catch (e) {
            console.error(e)
        }
    }

    // Form JSX.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isUpdatingAccountAvatar}
        >
            <Section>
                <FileInput label={t('labelProfilePicturOrLogo')} value={file} onChange={(val) => setFile(val)} />
                {file &&
                    <div className="relative h-64 w-full">
                        <Cropper
                            image={file ? URL.createObjectURL(file) : undefined}
                            crop={crop}
                            zoom={zoom}
                            aspect={1/1}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            zoomSpeed={0.1}
                            minZoom={0.2}
                            restrictPosition={false}
                        />
                    </div>
                }
                <div className="input-holder">
                    <label>{t('labelAvatarBorderColor')}</label>
                    <CirclePicker
                        color={avatarColor}
                        onChangeComplete={(color) => setAvatarColor(color.hex)}
                        colors={['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16']}
                    />
                </div>
            </Section>

            <div className={`input-holder`}>
                {isUpdatingAccountAvatar ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary"><SaveIcon />{t('save')}</Button>
                )}
            </div>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    )
}

export default memo(AccountAvatarForm)
